import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/GridLegacy";
import LinearProgress from "@mui/material/LinearProgress";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import { pageTitleSx } from "../../constants/designTokens";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Alert from "@mui/material/Alert";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { useLazyQueryTopBuyersByNdc } from "../../queries/report/hooks/useLazyQueryTopBuyersByNdc";
import { useLazyQueryTopProductsForBuyer } from "../../queries/report/hooks/useLazyQueryTopProductsForBuyer";
import { useLazyQueryConfirmedNotShippedOrders } from "../../queries/report/hooks/useLazyQueryConfirmedNotShippedOrders";
import { useLazyQueryShortageProducts } from "../../queries/report/hooks/useLazyQueryShortageProducts";
import { PHARMACIES_FOR_REPORTS_SELECT } from "../../queries/report/reportTypeDefs";

import "./reports.css";

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

/** YYYY-MM-DD from `<input type="date" />` → UTC day bounds for API */
function dateInputToUtcStart(isoDate: string): string | undefined {
  const s = isoDate.trim();
  if (!s) return undefined;
  return `${s}T00:00:00.000Z`;
}

function dateInputToUtcEnd(isoDate: string): string | undefined {
  const s = isoDate.trim();
  if (!s) return undefined;
  return `${s}T23:59:59.999Z`;
}

type BuyerPharmacyOption = { id: number; legalName: string };

type RunState = { status: "idle" | "queued" | "running"; startedAtMs?: number };

function getUserFriendlyReportError(error: any): string {
  const rawMessage = String(
    error?.message ??
      error?.networkError?.message ??
      error?.graphQLErrors?.[0]?.message ??
      "Failed to run report."
  );
  const lower = rawMessage.toLowerCase();
  if (
    lower.includes("too many requests") ||
    lower.includes("report queue window is active") ||
    lower.includes("retry in about")
  ) {
    return "Report window is temporarily busy due to scheduled jobs. Please retry in a moment.";
  }
  return rawMessage;
}

function getBusyWindowState(now: Date, intervalMinutes: number, windowMinutes: number) {
  const interval = Math.max(1, intervalMinutes);
  const window = Math.max(0, windowMinutes);
  if (window === 0) {
    return { inBusyWindow: false, retryAfterMs: 0 };
  }

  const minuteOffset = now.getUTCMinutes() % interval;
  if (minuteOffset >= window) {
    return { inBusyWindow: false, retryAfterMs: 0 };
  }

  const remainingWholeMinutes = window - minuteOffset - 1;
  const remainingMsThisMinute =
    (59 - now.getUTCSeconds()) * 1000 + (1000 - now.getUTCMilliseconds());
  return {
    inBusyWindow: true,
    retryAfterMs: remainingWholeMinutes * 60_000 + remainingMsThisMinute,
  };
}

function useQueuedIndicator(isLoading: boolean, runState: RunState): string | null {
  const [nowMs, setNowMs] = useState<number>(() => Date.now());

  useEffect(() => {
    if (!isLoading || !runState.startedAtMs) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 500);
    return () => window.clearInterval(id);
  }, [isLoading, runState.startedAtMs]);

  if (!isLoading || !runState.startedAtMs) return null;
  const elapsed = nowMs - runState.startedAtMs;

  // If it takes longer than a short threshold, assume it's waiting behind other DB work.
  if (elapsed >= 1200) {
    const seconds = Math.max(1, Math.round(elapsed / 1000));
    return `Queued / waiting for slot… (${seconds}s)`;
  }
  return "Starting…";
}

export const Reports: React.FunctionComponent = () => {
  const reportJobIntervalMinutes =
    Number(process.env.REACT_APP_REPORT_JOB_INTERVAL_MINUTES || 10) || 10;
  const reportBusyWindowMinutes =
    Number(process.env.REACT_APP_REPORT_BUSY_WINDOW_MINUTES || 3) || 3;

  const [ndc, setNdc] = useState("");
  const [topBuyersFrom, setTopBuyersFrom] = useState("");
  const [topBuyersTo, setTopBuyersTo] = useState("");
  const [buyerPharmacy, setBuyerPharmacy] = useState<BuyerPharmacyOption | null>(
    null
  );
  const [topProductsFrom, setTopProductsFrom] = useState("");
  const [topProductsTo, setTopProductsTo] = useState("");
  const [notShippedFrom, setNotShippedFrom] = useState("");
  const [notShippedTo, setNotShippedTo] = useState("");
  const [shortageFrom, setShortageFrom] = useState("");
  const [shortageTo, setShortageTo] = useState("");
  const [shortageBuyerPharmacy, setShortageBuyerPharmacy] =
    useState<BuyerPharmacyOption | null>(null);
  const [topBuyersRun, setTopBuyersRun] = useState<RunState>({ status: "idle" });
  const [topProductsRun, setTopProductsRun] = useState<RunState>({ status: "idle" });
  const [notShippedRun, setNotShippedRun] = useState<RunState>({ status: "idle" });
  const [shortageRun, setShortageRun] = useState<RunState>({ status: "idle" });
  const [busyClockMs, setBusyClockMs] = useState<number>(() => Date.now());

  const { data: pharmaciesSelectData, loading: pharmaciesSelectLoading } =
    useQuery(PHARMACIES_FOR_REPORTS_SELECT, {
      fetchPolicy: "cache-first",
    });

  const buyerPharmacyOptions: BuyerPharmacyOption[] =
    pharmaciesSelectData?.findManyPharmacy ?? [];

  const [runTopBuyers, topBuyersQuery] = useLazyQueryTopBuyersByNdc({
    fetchPolicy: "network-only",
  });
  const [runTopProducts, topProductsQuery] = useLazyQueryTopProductsForBuyer({
    fetchPolicy: "network-only",
  });
  const [runConfirmedNotShipped, confirmedNotShippedQuery] =
    useLazyQueryConfirmedNotShippedOrders({
      fetchPolicy: "network-only",
    });
  const [runShortageProducts, shortageProductsQuery] = useLazyQueryShortageProducts({
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    const id = window.setInterval(() => setBusyClockMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (topBuyersQuery.loading) {
      setTopBuyersRun((s) => (s.status === "idle" ? s : { ...s, status: "running" }));
    } else if (topBuyersQuery.called) {
      setTopBuyersRun({ status: "idle" });
    }
  }, [topBuyersQuery.loading, topBuyersQuery.called]);

  useEffect(() => {
    if (topProductsQuery.loading) {
      setTopProductsRun((s) => (s.status === "idle" ? s : { ...s, status: "running" }));
    } else if (topProductsQuery.called) {
      setTopProductsRun({ status: "idle" });
    }
  }, [topProductsQuery.loading, topProductsQuery.called]);

  useEffect(() => {
    if (confirmedNotShippedQuery.loading) {
      setNotShippedRun((s) => (s.status === "idle" ? s : { ...s, status: "running" }));
    } else if (confirmedNotShippedQuery.called) {
      setNotShippedRun({ status: "idle" });
    }
  }, [confirmedNotShippedQuery.loading, confirmedNotShippedQuery.called]);

  useEffect(() => {
    if (shortageProductsQuery.loading) {
      setShortageRun((s) => (s.status === "idle" ? s : { ...s, status: "running" }));
    } else if (shortageProductsQuery.called) {
      setShortageRun({ status: "idle" });
    }
  }, [shortageProductsQuery.loading, shortageProductsQuery.called]);

  const onRunTopBuyers = () => {
    const trimmed = ndc.trim();
    if (!trimmed) return;
    const from = dateInputToUtcStart(topBuyersFrom);
    const to = dateInputToUtcEnd(topBuyersTo);
    setTopBuyersRun({ status: "queued", startedAtMs: Date.now() });
    runTopBuyers({
      variables: {
        ndc: trimmed,
        limit: 50,
        ...(from != null ? { orderCreatedFrom: from } : {}),
        ...(to != null ? { orderCreatedTo: to } : {}),
      },
    });
  };

  const onRunTopProducts = () => {
    const id = buyerPharmacy?.id;
    if (id == null || !Number.isFinite(id)) return;
    const from = dateInputToUtcStart(topProductsFrom);
    const to = dateInputToUtcEnd(topProductsTo);
    setTopProductsRun({ status: "queued", startedAtMs: Date.now() });
    runTopProducts({
      variables: {
        buyerPharmacyId: id,
        limit: 50,
        ...(from != null ? { orderCreatedFrom: from } : {}),
        ...(to != null ? { orderCreatedTo: to } : {}),
      },
    });
  };

  const onRunConfirmedNotShipped = () => {
    const from = dateInputToUtcStart(notShippedFrom);
    const to = dateInputToUtcEnd(notShippedTo);
    setNotShippedRun({ status: "queued", startedAtMs: Date.now() });
    runConfirmedNotShipped({
      variables: {
        limit: 200,
        ...(from != null ? { orderCreatedFrom: from } : {}),
        ...(to != null ? { orderCreatedTo: to } : {}),
      },
    });
  };

  const onRunShortageProducts = () => {
    const from = dateInputToUtcStart(shortageFrom);
    const to = dateInputToUtcEnd(shortageTo);
    const buyerId = shortageBuyerPharmacy?.id;
    setShortageRun({ status: "queued", startedAtMs: Date.now() });
    runShortageProducts({
      variables: {
        limit: 50,
        ...(buyerId != null && Number.isFinite(buyerId) ? { buyerPharmacyId: buyerId } : {}),
        ...(from != null ? { orderCreatedFrom: from } : {}),
        ...(to != null ? { orderCreatedTo: to } : {}),
      },
    });
  };

  const topBuyers = topBuyersQuery.data?.topBuyersByNdc ?? [];
  const topProducts = topProductsQuery.data?.topProductsForBuyerPharmacy ?? [];
  const confirmedNotShippedRows =
    confirmedNotShippedQuery.data?.confirmedNotShippedOrders ?? [];
  const shortageRows = shortageProductsQuery.data?.shortageProducts ?? [];

  const topBuyersQueuedText = useQueuedIndicator(topBuyersQuery.loading, topBuyersRun);
  const topProductsQueuedText = useQueuedIndicator(topProductsQuery.loading, topProductsRun);
  const notShippedQueuedText = useQueuedIndicator(
    confirmedNotShippedQuery.loading,
    notShippedRun
  );
  const shortageQueuedText = useQueuedIndicator(shortageProductsQuery.loading, shortageRun);
  const busyWindow = useMemo(
    () =>
      getBusyWindowState(
        new Date(busyClockMs),
        reportJobIntervalMinutes,
        reportBusyWindowMinutes
      ),
    [busyClockMs, reportJobIntervalMinutes, reportBusyWindowMinutes]
  );
  const busyWindowSeconds = Math.max(1, Math.ceil(busyWindow.retryAfterMs / 1000));
  const busyWindowMessage = busyWindow.inBusyWindow
    ? `Scheduled jobs are running. Reports unlock in about ${busyWindowSeconds}s.`
    : null;

  const cellTextSx = {
    maxWidth: { xs: 120, sm: 160 },
    wordBreak: "break-word" as const,
    verticalAlign: "top" as const,
  };

  return (
    <Paper
      className="reportsContainer"
      elevation={1}
      sx={{
        p: { xs: 2, md: 3 },
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
      }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography component="h1" sx={{ ...pageTitleSx, mb: 1 }}>
            Admin reporting
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 900 }}>
            Administrator-only reports across the marketplace. Excludes canceled orders and
            failed shipments. NDC matching ignores dashes and formatting. Optional date
            filters use order placed time (order createdAt), inclusive UTC calendar days.
          </Typography>
        </Grid>

        <Grid item xs={12} sx={{ minWidth: 0 }}>
          <Accordion
            defaultExpanded
            disableGutters
            elevation={0}
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              overflow: "hidden",
              bgcolor: "background.paper",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon aria-label="show or hide report" />}
              sx={{
                px: 2,
                minHeight: 56,
                "& .MuiAccordionSummary-content": { my: 1.5 },
              }}
            >
              <Typography variant="h6" component="span">
                Shortage products (sold before, not listed now)
              </Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                px: 2,
                pb: 2,
                pt: 0,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Previously sold NDCs with no active, in-stock listing now. Buyer pharmacy optional.
              </Typography>
              {shortageProductsQuery.loading && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    size="small"
                    color="info"
                    label={shortageQueuedText ?? "Loading…"}
                    variant="outlined"
                  />
                </Stack>
              )}
              {shortageProductsQuery.loading && <LinearProgress />}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "flex-start" }}
                flexWrap="wrap"
                useFlexGap
              >
                <Autocomplete
                  sx={{ flex: { sm: "1 1 280px" }, minWidth: 0, maxWidth: "100%" }}
                  options={buyerPharmacyOptions}
                  loading={pharmaciesSelectLoading}
                  value={shortageBuyerPharmacy}
                  onChange={(_, v) => setShortageBuyerPharmacy(v)}
                  getOptionLabel={(p) => `${p.legalName} · ID ${p.id}`}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Buyer pharmacy (optional)"
                      size="small"
                      placeholder="All buyers if empty…"
                    />
                  )}
                />
                <TextField
                  label="From (optional)"
                  type="date"
                  value={shortageFrom}
                  onChange={(e) => setShortageFrom(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: { sm: "0 0 160px" }, minWidth: 0 }}
                />
                <TextField
                  label="To (optional)"
                  type="date"
                  value={shortageTo}
                  onChange={(e) => setShortageTo(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: { sm: "0 0 160px" }, minWidth: 0 }}
                />
                <Button
                  variant="contained"
                  onClick={onRunShortageProducts}
                  disabled={busyWindow.inBusyWindow}
                >
                  Run
                </Button>
              </Stack>
              {busyWindowMessage && <Alert severity="info">{busyWindowMessage}</Alert>}
              {shortageProductsQuery.error && (
                <Alert severity="error">
                  {getUserFriendlyReportError(shortageProductsQuery.error)}
                </Alert>
              )}
              <TableContainer
                className="reportsTableScroll"
                sx={{
                  maxHeight: { xs: 360, md: 420 },
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Table size="small" stickyHeader sx={{ width: "100%", tableLayout: "fixed" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: "14%" }}>NDC</TableCell>
                      <TableCell sx={{ width: "22%" }}>Drug name</TableCell>
                      <TableCell sx={{ width: "14%" }}>Strength</TableCell>
                      <TableCell align="right" sx={{ width: "8%" }}>
                        Orders
                      </TableCell>
                      <TableCell align="right" sx={{ width: "8%" }}>
                        Qty
                      </TableCell>
                      <TableCell align="right" sx={{ width: "10%" }}>
                        Total spent
                      </TableCell>
                      <TableCell align="right" sx={{ width: "12%" }}>
                        Min $ / unit
                      </TableCell>
                      <TableCell align="right" sx={{ width: "12%" }}>
                        Max $ / unit
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shortageRows.map((row: any, idx: number) => (
                      <TableRow key={`${row.ndc}-${idx}`}>
                        <TableCell sx={cellTextSx}>{row.ndc}</TableCell>
                        <TableCell sx={cellTextSx}>{row.drugName}</TableCell>
                        <TableCell sx={cellTextSx}>{row.strengths || "—"}</TableCell>
                        <TableCell align="right">{row.orderCount}</TableCell>
                        <TableCell align="right">{row.totalQuantity}</TableCell>
                        <TableCell align="right">${row.totalSpent}</TableCell>
                        <TableCell align="right">${row.minPricePaid}</TableCell>
                        <TableCell align="right">${row.maxPricePaid}</TableCell>
                      </TableRow>
                    ))}
                    {!shortageProductsQuery.loading &&
                      shortageRows.length === 0 &&
                      shortageProductsQuery.called && (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            No matching rows. Either every purchased NDC still has an active
                            in-stock listing, or there are no orders in the selected scope.
                          </TableCell>
                        </TableRow>
                      )}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid item xs={12} sx={{ minWidth: 0 }}>
          <Accordion
            defaultExpanded
            disableGutters
            elevation={0}
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              overflow: "hidden",
              bgcolor: "background.paper",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon aria-label="show or hide report" />}
              sx={{
                px: 2,
                minHeight: 56,
                "& .MuiAccordionSummary-content": { my: 1.5 },
              }}
            >
              <Typography variant="h6" component="span">
                Top buyers for NDC (platform-wide)
              </Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                px: 2,
                pb: 2,
                pt: 0,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {topBuyersQuery.loading && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    size="small"
                    color="info"
                    label={topBuyersQueuedText ?? "Loading…"}
                    variant="outlined"
                  />
                </Stack>
              )}
              {topBuyersQuery.loading && <LinearProgress />}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "flex-start" }}
                flexWrap="wrap"
                useFlexGap
              >
              <TextField
                label="NDC"
                value={ndc}
                onChange={(e) => setNdc(e.target.value)}
                size="small"
                sx={{ flex: { sm: "1 1 140px" }, minWidth: 0 }}
                placeholder="e.g. 12345-678-90"
              />
              <TextField
                label="From (optional)"
                type="date"
                value={topBuyersFrom}
                onChange={(e) => setTopBuyersFrom(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ flex: { sm: "0 0 160px" }, minWidth: 0 }}
              />
              <TextField
                label="To (optional)"
                type="date"
                value={topBuyersTo}
                onChange={(e) => setTopBuyersTo(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ flex: { sm: "0 0 160px" }, minWidth: 0 }}
              />
              <Button
                variant="contained"
                onClick={onRunTopBuyers}
                disabled={!ndc.trim() || busyWindow.inBusyWindow}
              >
                Run
              </Button>
              </Stack>
            {busyWindowMessage && <Alert severity="info">{busyWindowMessage}</Alert>}
            {topBuyersQuery.error && (
              <Alert severity="error">
                {getUserFriendlyReportError(topBuyersQuery.error)}
              </Alert>
            )}
            <TableContainer
              className="reportsTableScroll"
              sx={{
                maxHeight: { xs: 360, md: 420 },
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <Table size="small" stickyHeader sx={{ minWidth: 560, tableLayout: "fixed" }}>
                <colgroup>
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "16%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "7%" }} />
                  <col style={{ width: "7%" }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell>Buyer pharmacy</TableCell>
                    <TableCell>Pharmacy email</TableCell>
                    <TableCell>Buyer user email</TableCell>
                    <TableCell>Last order</TableCell>
                    <TableCell align="right">Min price</TableCell>
                    <TableCell align="right">Max price</TableCell>
                    <TableCell align="right">Orders</TableCell>
                    <TableCell align="right">Total spent</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topBuyers.map((row: any) => (
                    <TableRow key={row.buyerPharmacyId}>
                      <TableCell sx={cellTextSx}>{row.buyerPharmacyName}</TableCell>
                      <TableCell sx={cellTextSx}>{row.buyerPharmacyEmail}</TableCell>
                      <TableCell sx={cellTextSx}>{row.buyerUserEmail}</TableCell>
                      <TableCell sx={cellTextSx}>{formatDate(row.lastOrderDate)}</TableCell>
                      <TableCell align="right">${row.minPrice}</TableCell>
                      <TableCell align="right">${row.maxPrice}</TableCell>
                      <TableCell align="right">{row.orderCount}</TableCell>
                      <TableCell align="right">${row.totalSpent}</TableCell>
                    </TableRow>
                  ))}
                  {!topBuyersQuery.loading &&
                    topBuyers.length === 0 &&
                    topBuyersQuery.called && (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          No matching orders for this NDC.
                        </TableCell>
                      </TableRow>
                    )}
                </TableBody>
              </Table>
            </TableContainer>
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid item xs={12} sx={{ minWidth: 0 }}>
          <Accordion
            defaultExpanded
            disableGutters
            elevation={0}
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              overflow: "hidden",
              bgcolor: "background.paper",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon aria-label="show or hide report" />}
              sx={{
                px: 2,
                minHeight: 56,
                "& .MuiAccordionSummary-content": { my: 1.5 },
              }}
            >
              <Typography variant="h6" component="span">
                Top products for buyer pharmacy
              </Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                px: 2,
                pb: 2,
                pt: 0,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Select a buyer pharmacy from the list (sorted by legal name). You can type to
                search.
              </Typography>
              {topProductsQuery.loading && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    size="small"
                    color="info"
                    label={topProductsQueuedText ?? "Loading…"}
                    variant="outlined"
                  />
                </Stack>
              )}
              {topProductsQuery.loading && <LinearProgress />}
              <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "flex-start" }}
              flexWrap="wrap"
              useFlexGap
            >
              <Autocomplete
                sx={{ flex: { sm: "1 1 280px" }, minWidth: 0, maxWidth: "100%" }}
                options={buyerPharmacyOptions}
                loading={pharmaciesSelectLoading}
                value={buyerPharmacy}
                onChange={(_, v) => setBuyerPharmacy(v)}
                getOptionLabel={(p) => `${p.legalName} · ID ${p.id}`}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Buyer pharmacy"
                    size="small"
                    placeholder="Search or select…"
                  />
                )}
              />
              <TextField
                label="From (optional)"
                type="date"
                value={topProductsFrom}
                onChange={(e) => setTopProductsFrom(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ flex: { sm: "0 0 160px" }, minWidth: 0 }}
              />
              <TextField
                label="To (optional)"
                type="date"
                value={topProductsTo}
                onChange={(e) => setTopProductsTo(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ flex: { sm: "0 0 160px" }, minWidth: 0 }}
              />
              <Button
                variant="contained"
                onClick={onRunTopProducts}
                disabled={buyerPharmacy == null || busyWindow.inBusyWindow}
              >
                Run
              </Button>
              </Stack>
            {busyWindowMessage && <Alert severity="info">{busyWindowMessage}</Alert>}
            {topProductsQuery.error && (
              <Alert severity="error">
                {getUserFriendlyReportError(topProductsQuery.error)}
              </Alert>
            )}
            <TableContainer
              className="reportsTableScroll"
              sx={{
                maxHeight: { xs: 360, md: 420 },
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <Table size="small" stickyHeader sx={{ width: "100%", tableLayout: "fixed" }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: "22%" }}>NDC</TableCell>
                    <TableCell sx={{ width: "38%" }}>Drug name</TableCell>
                    <TableCell align="right" sx={{ width: "12%" }}>
                      Orders
                    </TableCell>
                    <TableCell align="right" sx={{ width: "14%" }}>
                      Quantity
                    </TableCell>
                    <TableCell align="right" sx={{ width: "14%" }}>
                      Total spent
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topProducts.map((row: any) => (
                    <TableRow key={row.productId}>
                      <TableCell sx={cellTextSx}>{row.ndc}</TableCell>
                      <TableCell sx={cellTextSx}>{row.drugName}</TableCell>
                      <TableCell align="right">{row.orderCount}</TableCell>
                      <TableCell align="right">{row.totalQuantity}</TableCell>
                      <TableCell align="right">${row.totalSpent}</TableCell>
                    </TableRow>
                  ))}
                  {!topProductsQuery.loading &&
                    topProducts.length === 0 &&
                    topProductsQuery.called && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No matching orders for this pharmacy.
                        </TableCell>
                      </TableRow>
                    )}
                </TableBody>
              </Table>
            </TableContainer>
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid item xs={12} sx={{ minWidth: 0 }}>
          <Accordion
            defaultExpanded
            disableGutters
            elevation={0}
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              overflow: "hidden",
              bgcolor: "background.paper",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon aria-label="show or hide report" />}
              sx={{
                px: 2,
                minHeight: 56,
                "& .MuiAccordionSummary-content": { my: 1.5 },
              }}
            >
              <Typography variant="h6" component="span">
                Confirmed — not shipped orders
              </Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                px: 2,
                pb: 2,
                pt: 0,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Seller-confirmed line items where shipping is still pending (confirmation
                pending or confirmed, shipping pending). Excludes canceled orders. One row
                per order line; amount is that line&apos;s total.
              </Typography>
              {confirmedNotShippedQuery.loading && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    size="small"
                    color="info"
                    label={notShippedQueuedText ?? "Loading…"}
                    variant="outlined"
                  />
                </Stack>
              )}
              {confirmedNotShippedQuery.loading && <LinearProgress />}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "flex-start" }}
                flexWrap="wrap"
                useFlexGap
              >
                <TextField
                  label="From (optional)"
                  type="date"
                  value={notShippedFrom}
                  onChange={(e) => setNotShippedFrom(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: { sm: "0 0 160px" }, minWidth: 0 }}
                />
                <TextField
                  label="To (optional)"
                  type="date"
                  value={notShippedTo}
                  onChange={(e) => setNotShippedTo(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: { sm: "0 0 160px" }, minWidth: 0 }}
                />
                <Button
                  variant="contained"
                  onClick={onRunConfirmedNotShipped}
                  disabled={busyWindow.inBusyWindow}
                >
                  Run
                </Button>
              </Stack>
              {busyWindowMessage && <Alert severity="info">{busyWindowMessage}</Alert>}
              {confirmedNotShippedQuery.error && (
                <Alert severity="error">
                  {getUserFriendlyReportError(confirmedNotShippedQuery.error)}
                </Alert>
              )}
              <TableContainer
                className="reportsTableScroll"
                sx={{
                  maxHeight: { xs: 360, md: 420 },
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Table
                  size="small"
                  stickyHeader
                  sx={{ minWidth: 640, tableLayout: "fixed", width: "100%" }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: "10%" }}>Order #</TableCell>
                      <TableCell sx={{ width: "22%" }}>Buyer</TableCell>
                      <TableCell sx={{ width: "22%" }}>Seller</TableCell>
                      <TableCell align="right" sx={{ width: "12%" }}>
                        Amount
                      </TableCell>
                      <TableCell sx={{ width: "18%" }}>Date</TableCell>
                      <TableCell sx={{ width: "16%" }}>Shipping status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {confirmedNotShippedRows.map((row: any) => (
                      <TableRow key={row.orderId}>
                        <TableCell>{row.orderNumber}</TableCell>
                        <TableCell sx={cellTextSx}>{row.buyerPharmacyName}</TableCell>
                        <TableCell sx={cellTextSx}>{row.sellerPharmacyName}</TableCell>
                        <TableCell align="right">${row.orderAmount}</TableCell>
                        <TableCell sx={cellTextSx}>{formatDate(row.createdAt)}</TableCell>
                        <TableCell sx={cellTextSx}>{row.shippingStatusLabel}</TableCell>
                      </TableRow>
                    ))}
                    {!confirmedNotShippedQuery.loading &&
                      confirmedNotShippedRows.length === 0 &&
                      confirmedNotShippedQuery.called && (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            No matching orders.
                          </TableCell>
                        </TableRow>
                      )}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default Reports;
